import { BACKEND_ERROR_MAP } from './backend-error-map';

function mapBackendError(message: string | string[], fallbackKey: string): string {
  // class-validator (ValidationPipe) returns `message` as a string[] of
  // constraint violations; flatten it so substring matching works.
  const text = Array.isArray(message) ? message.join(' ') : message;
  for (const [substring, key] of Object.entries(BACKEND_ERROR_MAP)) {
    if (text.includes(substring)) return key;
  }
  // The user deliberately never sees the raw backend text (it is English and
  // can leak internals), but swallowing it entirely made every unmapped error
  // indistinguishable — "Error desconocido" with no way to tell WHICH error.
  // Log it so a developer can read the real cause in the console and add a
  // BACKEND_ERROR_MAP entry for it.
  console.error('[extractError] unmapped backend error:', text);
  return fallbackKey;
}

/**
 * @param fallbackKey i18n key to use when the backend message does not match
 * any `BACKEND_ERROR_MAP` entry. Defaults to the generic `errors.unknown`;
 * pass a more specific key when the call site already knows what operation
 * failed (e.g. `errors.schedulingFailed`) so the fallback stays informative.
 */
export function extractError(error: unknown, fallbackKey = 'errors.unknown'): string {
  if (
    error &&
    typeof error === 'object' &&
    'error' in error &&
    error.error &&
    typeof error.error === 'object' &&
    'message' in error.error
  ) {
    return mapBackendError((error.error as { message: string | string[] }).message, fallbackKey);
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return mapBackendError((error as { message: string | string[] }).message, fallbackKey);
  }
  return fallbackKey;
}

/** Raw message text of an HTTP-error-like object, for pattern extraction. */
function rawMessage(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'error' in error &&
    error.error &&
    typeof error.error === 'object' &&
    'message' in error.error
  ) {
    const m = (error.error as { message: string | string[] }).message;
    return Array.isArray(m) ? m.join(' ') : String(m);
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: string | string[] }).message;
    return Array.isArray(m) ? m.join(' ') : String(m);
  }
  return '';
}

/**
 * Pulls the permission id out of a `PermissionsGuard` 403 message
 * (`Missing permission: prospects:create`). Returns null when the message is not
 * a missing-permission error.
 */
export function extractMissingPermission(error: unknown): string | null {
  const match = /Missing permission:\s*([\w:-]+)/.exec(rawMessage(error));
  return match ? match[1] : null;
}
